import {ApolloQueryResult, gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {coreObjectArchiveAction} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {assert} from 'Asserts';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import FieldActionButtons from 'metadata/FieldActionButtons';
import notifications from 'notifications';
import {
  SampleTypesListingQuery_allSampleTypes_SampleTypeNode as SampleType,
  SampleTypesListingQuery,
  SampleTypesListingQuery_Query as SampleTypesListingQueryQuery,
  SampleTypesListingQueryVariables,
} from 'sample-types/__generated/SampleTypesListingQuery';
import AddSampleTypeButton from 'sample-types/AddSampleTypeButton';
import {createSampleType} from 'sample-types/network/SampleTypeDAO';
import {Cell, EmptyCell, Header, HeaderCell, Row, Table} from 'sample-types/SampleTypesListingTable';
import FormInput from 'ui-library/bs/FormInput';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ErrorMessage from 'ui-library/ErrorMessage';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useFormInput from 'ui-library/hooks/useFormInput';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import strUtil from 'util/strUtil';
import {InputChangeEvent} from 'util/Types';

export const SAMPLE_TYPES_LISTING_QUERY = gql`
  query SampleTypesListingQuery @skipAudit @engComponent(name: STUDIES) {
    allSampleTypes {
      ... on SampleTypeNode {
        id
        name
        label
        createdAt
        archiveData {
          archived
          purpose
        }
      }
    }
    currentUser {
      ... on UserNode {
        id
        isBenchlingAdmin
        isTenantAdmin
      }
    }
  }
`;

const SAMPLE_TYPE_NAME_COLUMN_HEADER_TEXT = 'Sample Type';
const SAMPLE_TYPE_LABEL_COLUMN_HEADER_TEXT = 'Label';

enum EditState {
  VIEWING = 'VIEWING',
  PENDING_CREATE = 'PENDING_CREATE',
  PENDING_UPDATE = 'PENDING_UPDATE',
  CREATING = 'CREATING',
}

function QueryingSampleTypesDictionary(): JSX.Element {
  const queryResult = useBenchlingQuery<SampleTypesListingQuery, SampleTypesListingQueryVariables>(
    SAMPLE_TYPES_LISTING_QUERY,
    {renderCachedDataWhenRefetching: true}
  );
  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return <ErrorMessage error="Failed to load sample types" />;
  }
  const currentUser = queryResult.data.currentUser;
  assert(currentUser?.__typename === 'UserNode', `Non-user should not access this page.`);
  return (
    <SampleTypesDictionary
      sampleTypes={queryResult.data.allSampleTypes}
      isAdmin={Boolean(currentUser?.isBenchlingAdmin || currentUser?.isTenantAdmin)}
      refetch={queryResult.refetch}
    />
  );
}

function SampleTypesDictionary({
  sampleTypes,
  isAdmin,
  refetch,
}: {
  sampleTypes: Array<SampleType>;
  isAdmin: boolean;
  refetch: () => Promise<ApolloQueryResult<SampleTypesListingQueryQuery>>;
}): JSX.Element {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [editState, setEditState] = React.useState(EditState.VIEWING);
  const [truncateArchived, setTruncateArchived] = React.useState(true);

  async function onAddSampleType(name: string, label: string): Promise<void> {
    setEditState(EditState.PENDING_CREATE);
    try {
      await createSampleType({name, label});
    } catch (error) {
      setEditState(EditState.CREATING);
      throw error;
    }
    await refetch();
    setEditState(EditState.VIEWING);
    notifications.success(
      <>
        Sample type <strong>{name}</strong> added
      </>
    );
  }

  function onArchive(sampleType: SampleType): void {
    ArchiveModal.open({
      items: [sampleType],
      onArchive: async (itemIds: Array<string>, purpose: string) => {
        setEditState(EditState.PENDING_UPDATE);
        try {
          await coreObjectArchiveAction(itemIds, purpose);
        } catch (error) {
          setEditState(EditState.VIEWING);
          throw error;
        }
        await refetch();
        setEditState(EditState.VIEWING);
      },
    });
  }

  async function onUnarchive(sampleType: SampleType): Promise<void> {
    const unarchiveFn = getUnarchiveAction([sampleType.id]);
    setEditState(EditState.PENDING_UPDATE);
    try {
      await unarchiveFn([sampleType.id]);
    } catch (error) {
      setEditState(EditState.VIEWING);
      throw error;
    }
    await refetch();
    setEditState(EditState.VIEWING);
  }

  const [unArchivedSampleTypes, archivedSampleTypes] = useMemo(() => {
    const unArchived: Array<SampleType> = [];
    const archived: Array<SampleType> = [];
    for (const sampleType of sampleTypes) {
      if (sampleType.archiveData.archived) {
        archived.push(sampleType);
      } else {
        unArchived.push(sampleType);
      }
    }
    return [unArchived, archived];
  }, [sampleTypes]);

  const sortedSampleTypes = useMemo(() => sortSampleTypes(unArchivedSampleTypes), [unArchivedSampleTypes]);
  const sortedArchivedSampleTypes = useMemo(
    () => (truncateArchived ? archivedSampleTypes : sortSampleTypes(archivedSampleTypes)),
    [truncateArchived, archivedSampleTypes]
  );

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const isSearching = !strUtil.isEmpty(normalizedSearchQuery);
  const filteredSampleTypes = useMemo(
    () => (isSearching ? filterSampleTypes(sortedSampleTypes, normalizedSearchQuery) : sortedSampleTypes),
    [sortedSampleTypes, isSearching, normalizedSearchQuery]
  );
  const filteredArchivedSampleTypes = useMemo(
    () =>
      isSearching
        ? filterSampleTypes(sortedArchivedSampleTypes, normalizedSearchQuery)
        : sortedArchivedSampleTypes,
    [sortedArchivedSampleTypes, isSearching, normalizedSearchQuery]
  );

  const rows = filteredSampleTypes.map((sampleType) => (
    <ExistingSampleTypeRow
      key={sampleType.id}
      sampleType={sampleType}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      isDisabled={editState !== EditState.VIEWING}
      canEdit={isAdmin}
    />
  ));

  if ([EditState.CREATING, EditState.PENDING_CREATE].includes(editState)) {
    rows.push(
      <NewSampleTypeRow
        key="new"
        onCancel={() => {
          setEditState(EditState.VIEWING);
        }}
        onSubmit={onAddSampleType}
        isDisabled={editState !== EditState.CREATING}
      />
    );
  }

  if (truncateArchived && filteredArchivedSampleTypes.length > 0) {
    rows.push(
      <ArchivedSummaryRow
        key="showArchived"
        numArchived={filteredArchivedSampleTypes.length}
        onClick={() => {
          setTruncateArchived(false);
        }}
      />
    );
  } else {
    rows.push(
      ...filteredArchivedSampleTypes.map((sampleType) => (
        <ExistingSampleTypeRow
          key={sampleType.id}
          sampleType={sampleType}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          isDisabled={editState !== EditState.VIEWING}
          canEdit={isAdmin}
        />
      ))
    );
  }

  if (rows.length === 0 && editState === EditState.VIEWING) {
    rows.push(
      <EmptySampleTypeRow
        key="empty"
        message={isSearching ? 'No sample types found' : 'No sample types have been added'}
      />
    );
  }

  return (
    <div {...getTestProps({component: 'SampleTypesDictionary', element: 'root'})}>
      <div className={css(styles.tableControls)}>
        <PlainSearchInput
          className={css(styles.search)}
          clearType={ClearType.X}
          onChange={(e: InputChangeEvent) => {
            setSearchQuery(e.target.value);
          }}
          onEscape={() => {
            setSearchQuery('');
          }}
          placeholder="Search"
          value={searchQuery}
        />
        <Tooltip text={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
          <Button
            aria-label={EXPORT_AUDIT_LOG_DISPLAY_STRING}
            icon="audit-trail"
            onClick={() => {
              exportAuditLogs(sampleTypes.map((sampleType) => sampleType.id));
            }}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
          />
        </Tooltip>
      </div>
      <Table>
        <Header>
          <HeaderCell>
            {SAMPLE_TYPE_NAME_COLUMN_HEADER_TEXT}
            {isAdmin && (
              <AddSampleTypeButton
                onClick={() => {
                  setEditState(EditState.CREATING);
                }}
                isDisabled={editState !== EditState.VIEWING}
              />
            )}
          </HeaderCell>
          <HeaderCell>{SAMPLE_TYPE_LABEL_COLUMN_HEADER_TEXT}</HeaderCell>
        </Header>
        {rows}
      </Table>
    </div>
  );
}

/** @package Exported for testing */
export function ExistingSampleTypeRow({
  sampleType,
  onArchive,
  onUnarchive,
  isDisabled,
  canEdit,
}: {
  sampleType: SampleType;
  onArchive: (sampleType: SampleType) => void;
  onUnarchive: (sampleType: SampleType) => void;
  isDisabled: boolean;
  canEdit: boolean;
}): JSX.Element {
  return (
    <Row {...getTestProps({component: 'SampleTypesDictionary', element: 'sampleTypeRow'})}>
      <Cell>
        <div className={sampleType.archiveData.archived ? css(styles.archivedField) : undefined}>
          {sampleType.name}
          {sampleType.archiveData.archived && ` (${sampleType.archiveData.purpose})`}
        </div>
      </Cell>
      <Cell>
        <div className={css(styles.archiveActionButtonWrapper)}>
          <div className={sampleType.archiveData.archived ? css(styles.archivedField) : undefined}>
            {sampleType.label}
          </div>
          {canEdit && (
            <FieldActionButtons
              isEditing={false}
              unarchive={
                sampleType.archiveData.archived
                  ? () => {
                      onUnarchive(sampleType);
                    }
                  : undefined
              }
              isUnarchiveEnabled={!isDisabled}
              archive={
                sampleType.archiveData.archived
                  ? undefined
                  : () => {
                      onArchive(sampleType);
                    }
              }
              isArchiveEnabled={!isDisabled}
            />
          )}
        </div>
      </Cell>
    </Row>
  );
}

/** @package Exported for testing */
export function ArchivedSummaryRow({
  numArchived,
  onClick,
}: {
  numArchived: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <Row key="empty">
      <EmptyCell>
        <a
          href=""
          onClick={(e) => {
            e.preventDefault();
            onClick();
          }}
        >
          Show archived sample types ({numArchived})
        </a>
      </EmptyCell>
    </Row>
  );
}

/** @package Exported for testing */
export function EmptySampleTypeRow({message}: {message: string}): JSX.Element {
  return (
    <Row key="empty">
      <EmptyCell>{message}</EmptyCell>
    </Row>
  );
}

/** @package Exported for testing */
export function NewSampleTypeRow({
  onCancel,
  onSubmit,
  isDisabled,
}: {
  onCancel: () => void;
  onSubmit: (name: string, label: string) => Promise<void>;
  isDisabled: boolean;
}): JSX.Element {
  const nameInput = useFormInput('');
  const labelInput = useFormInput('');

  return (
    <Row isBeingEdited={true}>
      <Cell>
        <FormInput
          type="text"
          aria-label={SAMPLE_TYPE_NAME_COLUMN_HEADER_TEXT}
          {...nameInput}
          disabled={isDisabled}
        />
      </Cell>
      <Cell>
        <div className={css(styles.editActionButtonWrapper)}>
          <FormInput
            type="text"
            aria-label={SAMPLE_TYPE_LABEL_COLUMN_HEADER_TEXT}
            {...labelInput}
            disabled={isDisabled}
          />
          <FieldActionButtons
            isEditing={true}
            cancel={onCancel}
            complete={() => onSubmit(nameInput.value, labelInput.value)}
            isCompleteEnabled={
              !isDisabled && !strUtil.isEmpty(nameInput.value) && !strUtil.isEmpty(labelInput.value)
            }
            isCancelEnabled={!isDisabled}
          />
        </div>
      </Cell>
    </Row>
  );
}

function filterSampleTypes(sampleTypes: Array<SampleType>, searchQuery: string): Array<SampleType> {
  return sampleTypes.filter(
    (sampleType) =>
      sampleType.name.toLowerCase().includes(searchQuery) ||
      sampleType.label.toLowerCase().includes(searchQuery)
  );
}

function sortSampleTypes(sampleTypes: Array<SampleType>): Array<SampleType> {
  // eslint-disable-next-line benchlint/no-date-parse
  return [...sampleTypes].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

const styles = StyleSheet.create({
  editActionButtonWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '14px',
    width: '100%',
  },
  archiveActionButtonWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  archivedField: {
    opacity: 0.6,
  },
  search: {
    width: 300,
    verticalAlign: 'middle',
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginTop: '6px',
    marginBottom: '16px',
    alignItems: 'center',
  },
});

export default QueryingSampleTypesDictionary;
